import { useState } from "react";

import {
  CoilType,
  DuctType,
  HeatingNetworkType,
  type Coil,
  type FullAirHandlerUnit,
  type HeatingNetwork,
} from "@eisox/api/core-api";
import { ActionButton, Field, Heading, Modal, ScrollArea, Select } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon, BinIcon, NetworkIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";

const NAME = "HydraulicCoilAssociationDialog";

const COIL_TITLE: Record<CoilType, () => string> = {
  [CoilType.HYDRAULIC_HEATING]: m.air_handler_unit_coil_hydraulic_heating,
  [CoilType.HYDRAULIC_COOLING]: m.air_handler_unit_coil_hydraulic_cooling,
  [CoilType.ELECTRIC]: m.air_handler_unit_coil_electric,
  [CoilType.HYDRAULIC_CHANGEOVER]: m.air_handler_unit_coil_hydraulic_changeover,
  [CoilType.CHILLER]: m.air_handler_unit_coil_chiller,
};

const DUCT_LABEL: Record<DuctType, () => string> = {
  [DuctType.EXHAUST]: m.exhaust,
  [DuctType.RETURN]: m.return,
  [DuctType.SUPPLY]: m.supply,
  [DuctType.OUTSIDE]: m.outside,
};

interface CoilEntry {
  airHandlerUnitId: string;
  airHandlerUnitName: string;
  coil: Coil;
}

const HYDRAULIC_COIL_TYPES = [CoilType.HYDRAULIC_HEATING, CoilType.HYDRAULIC_COOLING, CoilType.HYDRAULIC_CHANGEOVER];

const collectCoils = (airHandlerUnits: FullAirHandlerUnit[]): CoilEntry[] =>
  airHandlerUnits.flatMap(ahu =>
    (ahu.coils ?? [])
      .filter(coil => HYDRAULIC_COIL_TYPES.includes(coil.coilType))
      .map(coil => ({ airHandlerUnitId: ahu.id, airHandlerUnitName: ahu.name, coil })),
  );

const collectHeatingNetworks = (heatingNetworks: HeatingNetwork[]): HeatingNetwork[] =>
  heatingNetworks.filter(network => network.type === HeatingNetworkType.CENTRAL_AIR_HANDLING_UNIT);

interface HydraulicCoilAssociationDialogProps extends Modal.RootProps {
  buildingId: string;
  children?: React.ReactNode;
}

const HydraulicCoilAssociationDialog: React.FC<HydraulicCoilAssociationDialogProps> = ({
  buildingId,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { boilerRooms, heatingNetworks: allHeatingNetworks } = useBuildingContext(NAME);
  const { airHandlerUnits } = useAirHandlerUnitContext(NAME);
  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const boilerRoomNameById = new Map(boilerRooms.map(boilerRoom => [boilerRoom.id, boilerRoom.name]));

  const coilEntries = collectCoils(airHandlerUnits);

  const heatingNetworks = collectHeatingNetworks(allHeatingNetworks);

  const [selectedCoilId, setSelectedCoilId] = useState<string | undefined>(coilEntries[0]?.coil.id);

  // "" means no association; `null` rides the PATCH to clear one.
  const defaultAssociations = Object.fromEntries(
    coilEntries.map(entry => [entry.coil.id, entry.coil.heatingNetworkId ?? ""]),
  );
  const form = useAppForm({
    defaultValues: { associations: defaultAssociations },
    onSubmit: async ({ formApi, value }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const dirtyAssociations = dirty.associations ?? {};
      const dirtyEntries = coilEntries.filter(entry => entry.coil.id in dirtyAssociations);
      const byAirHandlerUnit = new Map<string, CoilEntry[]>();
      for (const entry of dirtyEntries) {
        byAirHandlerUnit.set(entry.airHandlerUnitId, [...(byAirHandlerUnit.get(entry.airHandlerUnitId) ?? []), entry]);
      }
      // Each failed PATCH raises its own error dialog; keep the form open unless every unit succeeded.
      const results = await Promise.allSettled(
        [...byAirHandlerUnit.entries()].map(([airHandlerUnitId, entries]) =>
          mutateAsync({
            airHandlerUnitId,
            body: {
              coils: entries.map(entry => ({
                id: entry.coil.id,
                heatingNetworkId: value.associations[entry.coil.id] || null,
              })),
            },
          }),
        ),
      );
      if (results.some(result => result.status === "rejected")) return;
      formApi.reset(value);
      setOpen(false);
    },
  });

  // The form and the selected coil outlive the modal content, so reopening must restore both.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      form.reset();
      setSelectedCoilId(coilEntries[0]?.coil.id);
    }
    setOpen(next);
  };

  const selectedEntry = coilEntries.find(entry => entry.coil.id === selectedCoilId);

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content className="min-h-125">
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={m.hydraulic_coil_association()}
              subtitle={m.air_handler_unit_hydraulic_coil_dialog_subtitle()}
              icon={<NetworkIcon />}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              <form.SubmitButton label={m.save()} icon={<ArrowRightIcon />} />
            </Modal.Header>
            <div className="min-h-0 gap-10 flex flex-1">
              <div className="gap-2.5 flex flex-1 flex-col">
                <Heading as="h3" size={4}>
                  {m.air_handler_unit_hydraulic_coils()}
                </Heading>
                <ScrollArea>
                  <ToggleGroup.Root
                    type="single"
                    orientation="vertical"
                    value={selectedCoilId}
                    onValueChange={coilId => {
                      if (coilId) setSelectedCoilId(coilId);
                    }}
                  >
                    {coilEntries.map(entry => (
                      <ToggleGroup.Item
                        key={entry.coil.id}
                        value={entry.coil.id}
                        className="gap-1.25 flex w-full cursor-pointer flex-col items-start rounded-[5px] p-[7.5px] text-base focus-visible:bg-primary-gray data-[state=on]:bg-primary-gray data-[state=on]:text-primary-blue"
                      >
                        <span>{`${COIL_TITLE[entry.coil.coilType]()} - ${DUCT_LABEL[entry.coil.type]()}`}</span>
                        <p className="text-sm text-primary-darker-gray italic">{entry.airHandlerUnitName}</p>
                      </ToggleGroup.Item>
                    ))}
                  </ToggleGroup.Root>
                </ScrollArea>
              </div>
              <div className="flex-1">
                {selectedEntry && (
                  <form.AppField name={`associations.${selectedEntry.coil.id}`}>
                    {field => (
                      <Field.Root>
                        <Field.Label>{m.air_handler_unit_associated_network()}</Field.Label>
                        <div className="gap-2.5 flex w-full">
                          <Select.Root
                            multiple={false}
                            value={field.state.value}
                            onValueChange={value => field.handleChange(value)}
                          >
                            <Select.Trigger className="flex-1">
                              <Select.Value placeholder={m.air_handler_unit_select_network()} />
                            </Select.Trigger>
                            <Select.Content>
                              <Select.Input />
                              <Select.Viewport>
                                {heatingNetworks.map(network => {
                                  const parentName = boilerRoomNameById.get(network.boilerRoomId);
                                  return (
                                    <Select.Item key={network.id} value={network.id}>
                                      {network.name}
                                      {parentName && (
                                        <span className="text-sm text-primary-dark-gray">{parentName}</span>
                                      )}
                                      <Select.ItemText>{network.name}</Select.ItemText>
                                    </Select.Item>
                                  );
                                })}
                              </Select.Viewport>
                            </Select.Content>
                          </Select.Root>
                          <button
                            type="button"
                            aria-label={m.air_handler_unit_clear_network_association()}
                            className="*:size-5 cursor-pointer"
                            onClick={() => field.handleChange("")}
                          >
                            <BinIcon />
                          </button>
                        </div>
                      </Field.Root>
                    )}
                  </form.AppField>
                )}
              </div>
            </div>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

HydraulicCoilAssociationDialog.displayName = NAME;

export { HydraulicCoilAssociationDialog };
export type { HydraulicCoilAssociationDialogProps };
